'use client'

import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react'
import { RecaptchaSettingQuery } from '@segway/core/src/graphql/generated/graphql'
import ReCAPTCHA from 'react-google-recaptcha'

interface GoogleReCaptchaProps {
  rendering: NonNullable<RecaptchaSettingQuery['recaptchaSetting']>['rendering']
  onChange?: (value: string | null) => void
  asyncScriptOnLoad?: () => void
  className?: string
}

export interface GoogleReCaptchaRef {
  reset: () => void
  // 执行验证，返回验证token或null
  execute: () => void
  executeAsync: () => Promise<string | null>
  getValue: () => string | null
}

const GoogleReCaptcha = forwardRef<GoogleReCaptchaRef, GoogleReCaptchaProps>(
  ({ rendering, onChange, asyncScriptOnLoad, className = '' }, ref) => {
    const recaptchaRef = useRef<ReCAPTCHA>(null)

    useImperativeHandle(ref, () => ({
      reset: () => {
        recaptchaRef.current?.reset()
        onChange?.(null)
      },
      execute: () => {
        recaptchaRef.current?.execute()
      },
      executeAsync: async () => {
        try {
          if (recaptchaRef.current) {
            const token = recaptchaRef.current?.executeAsync()
            return token
          }

          return null
        } catch (error) {
          console.error('ReCAPTCHA execution failed:', error)
          return null
        }
      },
      getValue: () => {
        if (recaptchaRef.current) {
          return recaptchaRef.current?.getValue()
        }

        return null
      },
    }))

    const handleChange = useCallback(
      (token: string | null) => {
        onChange?.(token)
      },
      [onChange],
    )

    return rendering?.sitekey ? (
      <div className={className}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={rendering.sitekey}
          onChange={handleChange}
          theme={rendering.theme as 'dark' | 'light' | undefined}
          size={rendering.size as 'compact' | 'normal' | 'invisible' | undefined}
          badge={rendering.badge as 'bottomright' | 'bottomleft' | 'inline' | undefined}
          hl={rendering.hl || undefined}
          asyncScriptOnLoad={asyncScriptOnLoad}
        />
      </div>
    ) : null
  },
)

GoogleReCaptcha.displayName = 'GoogleReCaptcha'

export default GoogleReCaptcha
